import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, Brain, Sparkles, TrendingUp, Heart, BookOpen, 
  Lightbulb, Target, Award, RefreshCw, Lock, MessageCircle,
  Calendar, Flame, ChevronRight
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const INSIGHT_ICONS = {
  'pattern': TrendingUp,
  'theme': Sparkles,
  'breakthrough_prompt': Lightbulb,
  'milestone': Award,
  'correlation': Brain
};

const INSIGHT_COLORS = {
  'pattern': { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-600', border: 'border-teal-200 dark:border-teal-800' },
  'theme': { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-600', border: 'border-teal-200 dark:border-teal-800' },
  'breakthrough_prompt': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600', border: 'border-amber-200 dark:border-amber-800' },
  'milestone': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600', border: 'border-green-200 dark:border-green-800' },
  'correlation': { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-600', border: 'border-rose-200 dark:border-rose-800' }
};

const DATA_SOURCE_LABELS = {
  'journal': { icon: BookOpen, label: 'Journal' },
  'mood': { icon: Heart, label: 'Mood' },
  'modules': { icon: Brain, label: 'Modules' },
  'kindness': { icon: Flame, label: 'Kindness' }
};

const InsightsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [insights, setInsights] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dataSummary, setDataSummary] = useState(null);
  const [lastAnalyzed, setLastAnalyzed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [isPremium, setIsPremium] = useState(true);
  const [insufficientData, setInsufficientData] = useState(false);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      const response = await axios.get(`${API}/insights/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(response.data.insights || []);
      setLastAnalyzed(response.data.last_analyzed);
      setDataSummary(response.data.data_summary);
      setIsPremium(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsPremium(false);
      } else {
        console.error("Error fetching insights:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.get(`${API}/insights/analyze`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.insufficient_data) {
        setInsufficientData(true);
        setSummary(response.data.summary);
        setInsights([]);
        toast.info("Keep journaling and tracking your mood to unlock insights!");
      } else {
        setInsights(response.data.insights || []);
        setSummary(response.data.summary);
        setLastAnalyzed(response.data.last_analyzed);
        setInsufficientData(false);
        toast.success("✨ New insights discovered!");
      }
    } catch (error) {
      toast.error("Unable to analyze patterns. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const markAsRead = async (insightId) => {
    try {
      await axios.post(`${API}/insights/${insightId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(prev => prev.map(i => 
        i.id === insightId ? { ...i, is_read: true } : i
      ));
    } catch (error) {
      console.error("Error marking insight as read:", error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-teal-500/20 flex items-center justify-center">
            <Brain className="w-8 h-8 text-teal-600" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  // Non-premium user view
  if (!isPremium) {
    return (
      <div className="min-h-screen hero-gradient" data-testid="insights-page-locked">
        <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-teal-600" strokeWidth={1.5} />
              <span className="font-heading text-lg font-semibold">Pattern Insights</span>
            </div>
            <div className="w-24"></div>
          </div>
        </nav>

        <main className="max-w-4xl mx-auto px-6 py-12">
          <Card className="glass-card rounded-3xl border-0 text-center p-12">
            <div className="w-20 h-20 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-teal-600" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-foreground mb-4">
              Unlock Pattern Recognition AI
            </h1>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Premium members get access to AI-powered insights that analyze your journal entries, 
              mood patterns, and growth journey to reveal meaningful patterns and breakthrough prompts.
            </p>
            <Button onClick={() => navigate("/subscription")} className="btn-primary">
              Go Premium
            </Button>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient" data-testid="insights-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-teal-600" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Pattern Insights</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <Brain className="w-10 h-10 text-teal-600" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Your Growth Patterns
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            AI-powered insights from your journal, mood, and journey data
          </p>
        </div>

        {/* Data Summary */}
        {dataSummary && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card className="glass-card rounded-2xl border-0">
              <CardContent className="p-4 text-center">
                <BookOpen className="w-6 h-6 text-teal-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{dataSummary.journal_entries}</p>
                <p className="text-xs text-muted-foreground">Journal Entries</p>
              </CardContent>
            </Card>
            <Card className="glass-card rounded-2xl border-0">
              <CardContent className="p-4 text-center">
                <Heart className="w-6 h-6 text-rose-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{dataSummary.mood_entries}</p>
                <p className="text-xs text-muted-foreground">Mood Logs</p>
              </CardContent>
            </Card>
            <Card className="glass-card rounded-2xl border-0">
              <CardContent className="p-4 text-center">
                <Flame className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{dataSummary.kindness_declarations}</p>
                <p className="text-xs text-muted-foreground">Kind Acts</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analyze Button */}
        <Card className="glass-card rounded-2xl border-0 mb-8 bg-gradient-to-r from-teal-50/50 to-emerald-50/50 dark:from-teal-950/20 dark:to-emerald-950/20">
          <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Sparkles className="w-10 h-10 text-teal-600" />
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground">
                  Discover New Patterns
                </h3>
                <p className="text-sm text-muted-foreground">
                  {lastAnalyzed 
                    ? `Last analyzed: ${formatDate(lastAnalyzed)}`
                    : "Run your first analysis to discover insights"
                  }
                </p>
              </div>
            </div>
            <Button
              onClick={runAnalysis}
              disabled={analyzing}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="analyze-patterns-btn"
            >
              {analyzing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  Analyze My Patterns
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Summary Card */}
        {summary && !insufficientData && (
          <Card className="glass-card rounded-2xl border-0 mb-8 bg-amber-50/30 dark:bg-amber-950/10">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                    {summary.overall_theme}
                  </h3>
                  <p className="text-foreground/80 leading-relaxed">
                    {summary.encouragement}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Insufficient Data Message */}
        {insufficientData && summary && (
          <Card className="glass-card rounded-2xl border-0 mb-8 text-center">
            <CardContent className="p-8">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                {summary.overall_theme}
              </h3>
              <p className="text-muted-foreground mb-4">
                {summary.encouragement}
              </p>
              <p className="text-sm text-muted-foreground">
                Data points: {summary.data_points} / {summary.minimum_required} minimum
              </p>
            </CardContent>
          </Card>
        )}

        {/* Insights List */}
        {insights.length > 0 && (
          <div className="space-y-4">
            <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
              Your Insights
            </h2>
            {insights.map((insight) => {
              const colors = INSIGHT_COLORS[insight.insight_type] || INSIGHT_COLORS.pattern;
              const Icon = INSIGHT_ICONS[insight.insight_type] || TrendingUp;
              
              return (
                <Card 
                  key={insight.id}
                  className={`glass-card rounded-2xl border ${colors.border} transition-all duration-300 ${
                    !insight.is_read ? 'ring-2 ring-teal-400/50' : ''
                  }`}
                  onClick={() => !insight.is_read && markAsRead(insight.id)}
                  data-testid={`insight-${insight.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} capitalize`}>
                            {insight.insight_type.replace('_', ' ')}
                          </span>
                          {!insight.is_read && (
                            <span className="text-xs bg-teal-500 text-white px-2 py-0.5 rounded-full">
                              New
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground ml-auto">
                            {Math.round(insight.confidence * 100)}% confidence
                          </span>
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                          {insight.title}
                        </h3>
                        <p className="text-foreground/80 leading-relaxed mb-3">
                          {insight.description}
                        </p>
                        
                        {/* Suggested Action */}
                        {insight.suggested_action && (
                          <div className="bg-muted/30 rounded-lg p-3 mb-3">
                            <p className="text-sm text-foreground/70 flex items-start gap-2">
                              <Target className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                              <span>{insight.suggested_action}</span>
                            </p>
                          </div>
                        )}
                        
                        {/* Data Sources */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-muted-foreground">Based on:</span>
                          {insight.data_sources?.map((source) => {
                            const sourceInfo = DATA_SOURCE_LABELS[source];
                            if (!sourceInfo) return null;
                            const SourceIcon = sourceInfo.icon;
                            return (
                              <span 
                                key={source}
                                className="text-xs bg-muted/50 px-2 py-1 rounded-full flex items-center gap-1"
                              >
                                <SourceIcon className="w-3 h-3" />
                                {sourceInfo.label}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {insights.length === 0 && !insufficientData && !analyzing && (
          <Card className="glass-card rounded-2xl border-0 text-center">
            <CardContent className="p-12">
              <div className="w-16 h-16 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                Ready to Discover Your Patterns?
              </h3>
              <p className="text-muted-foreground mb-6">
                Click "Analyze My Patterns" above to have AI examine your journey data
                and reveal meaningful insights about your growth.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Quick Links */}
        <div className="mt-10 grid grid-cols-2 gap-4">
          <Card 
            className="glass-card rounded-2xl border-0 cursor-pointer hover:scale-[1.02] transition-all"
            onClick={() => navigate("/journal")}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-teal-600" />
                <span className="font-medium text-foreground">Add Journal Entry</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </CardContent>
          </Card>
          <Card 
            className="glass-card rounded-2xl border-0 cursor-pointer hover:scale-[1.02] transition-all"
            onClick={() => navigate("/mood-progress")}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Heart className="w-5 h-5 text-rose-600" />
                <span className="font-medium text-foreground">View Mood Trends</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default InsightsPage;
